import React, { FunctionComponent, useEffect, useState } from 'react';
import { FlightButton, FlightAvatar, FlightDropdown, FlightModal } from '@flybits/design-system';
import './NavBarMenu.scss';
import { Link } from 'react-router-dom';
import { Organization } from '../../model/organizations/organizations';
import { User } from '../../model/users/users';
import useRoleAccess from 'src/hooks/useRoleAccess';
import { getUserRoles } from 'src/helpers/auth.helper';
import LoadingIcon from 'src/components/Shared/LoadingIcon/LoadingIcon';
import OrganizationService from 'src/services/organization.service';

// Translation
import { useTranslation } from 'react-i18next';
import { handleKeyDownHelper } from 'src/helpers/general.helper';

interface Props {
  currentOrganization: Organization | null;
  user: User | null;
  handleSwitchOrganizations: () => void;
  handleManageFlybitsAccount: () => void;
  initSignOut: () => void;
}
const NavBarMenu: FunctionComponent<Props> = (props: Props) => {
  const { currentOrganization, user, handleSwitchOrganizations, handleManageFlybitsAccount, initSignOut } = props;

  const { t } = useTranslation();
  // Removed unused `history` variable declaration.

  const DEFAULT_CLASS = 'flight-nav';
  const HEADER_CLASS = `${DEFAULT_CLASS}__header`;
  const CONTENT_CLASS = 'org_details__content';
  const LOADING_CLASS = 'org_details__loading'; // Use the same content class from OrganizationDetails.scss

  const [isActiveDropdown, setActiveDropDown] = useState(false);
  const [isLeavingOrganization, setLeavingOrganization] = useState(false);
  const [isLeavingLoading, setIsLeavingLoading] = useState(false);
  const [leaveOrgError, setLeaveOrgError] = useState('');

  const [currentRole, setCurrentRole] = useState(['member']);
  useEffect(() => {
    async function getRole() {
      const savedRoles = await getUserRoles();
      const roles = !currentOrganization || !currentOrganization.roles.length ? savedRoles : currentOrganization.roles;
      setCurrentRole(roles);
    }
    getRole();
  }, [isActiveDropdown]);
  const hasAccess = (required: string[]) => useRoleAccess(required, currentRole);
  const toggleDropdown = () => {
    setActiveDropDown(!isActiveDropdown);
  };
  const clickSwitchOrganizations = () => {
    setActiveDropDown(false);
    handleSwitchOrganizations();
  };
  const clickManageFlybitsAccount = () => {
    setActiveDropDown(false);
    handleManageFlybitsAccount();
  };
  const handleInitSignOut = () => {
    setActiveDropDown(false);
    initSignOut();
  };

  const resetErrors = () => {
    setLeaveOrgError('');
  };

  const leaveOrganization = async () => {
    resetErrors();
    setIsLeavingLoading(true);
    try {
      if (!currentOrganization?.id) {
        throw new Error('No organization ID found');
      }
      const organizationServiceManager = new OrganizationService();
      await organizationServiceManager.leaveOrganization(currentOrganization.id);

      setIsLeavingLoading(false);
      setLeavingOrganization(false);
      handleSwitchOrganizations();
    } catch (error: any) {
      console.error(error);
      setIsLeavingLoading(false);
      setLeaveOrgError(error?.response?.data?.message || 'Failed to leave organization.');
    }
  };

  function clickLeaveOrganization() {
    setLeavingOrganization(true);
    setActiveDropDown(false);
  }

  return (
    <div className="custom__navigation">
      <div className={`${HEADER_CLASS}__user ${currentOrganization?.name ? 'has-organization' : ''}`}>
        <FlightDropdown
          className={`${HEADER_CLASS}__user__dropdown`}
          isActive={isActiveDropdown}
          handleClickOutside={toggleDropdown}
          handleTriggerClick={toggleDropdown}
          isControlledByIsActive
          autoFocus
          maxWidth={'228px'}
          trigger={
            <div
              role={'button'}
              tabIndex={0}
              onClick={toggleDropdown}
              onKeyDown={handleKeyDownHelper(toggleDropdown, toggleDropdown)}
            >
              <FlightAvatar
                className={`${HEADER_CLASS}__user__avatar`}
                firstName={user?.givenName}
                lastName={user?.familyName}
                email={user?.username}
              />
              <p className="trigger-org-name" role="contentinfo">
                {currentOrganization?.name}
              </p>
            </div>
          }
        >
          <div className={`${HEADER_CLASS}__user__profile`}>
            <FlightAvatar
              className={`${HEADER_CLASS}__user__profile__avatar`}
              firstName={user?.givenName}
              lastName={user?.familyName}
              email={user?.username}
            />
            {user?.givenName && user?.familyName ? (
              <div
                aria-label={`User name: ${`${user?.givenName} ${user?.familyName}`}`}
                className={`${HEADER_CLASS}__user__profile__name`}
                tabIndex={0}
              >{`${user?.givenName} ${user?.familyName}`}</div>
            ) : null}
            {user?.username ? (
              <div
                aria-label={`User e-mail: ${`${user?.givenName} ${user?.familyName}`}`}
                className={`${HEADER_CLASS}__user__profile__email`}
              >
                {user?.username}
              </div>
            ) : null}

            {currentOrganization?.name ? (
              <div className={`${HEADER_CLASS}__user__profile__org`}>
                {currentOrganization?.imageUrl &&
                  currentOrganization?.imageUrl !== 'https://controltower.flybits.com/' && (
                    <img
                      src={currentOrganization?.imageUrl}
                      alt={`Image of: ${currentOrganization?.name}`}
                      role="presentation"
                      width={16}
                    />
                  )}
                {currentOrganization?.name}#{currentOrganization?.id}
              </div>
            ) : null}

            <div className={`${HEADER_CLASS}__user__profile__group`} role="navigation" aria-label="You grouping">
              {currentOrganization?.name && <h5>{t('general.menu.sections.you', 'You')}</h5>}
              <FlightButton
                className={`${HEADER_CLASS}__user__profile__links`}
                label={t('general.menu.labels.manage', 'Manage your Flybits account')}
                theme="link"
                onClick={clickManageFlybitsAccount}
                onKeyDown={handleKeyDownHelper(clickManageFlybitsAccount, toggleDropdown)}
              />
              {user?.confirmed === true && currentOrganization?.name && (
                <FlightButton
                  className={`${HEADER_CLASS}__user__profile__links`}
                  label={t('general.menu.labels.switch', 'Switch organizations')}
                  theme="link"
                  onClick={clickSwitchOrganizations}
                  onKeyDown={handleKeyDownHelper(clickSwitchOrganizations, toggleDropdown)}
                />
              )}
              {currentOrganization?.name && !hasAccess(['owner']) && (
                <FlightButton
                  className={`${HEADER_CLASS}__user__profile__links`}
                  label={t('btn-leave', 'Leave organization')}
                  theme="link"
                  onClick={clickLeaveOrganization}
                  onKeyDown={handleKeyDownHelper(clickLeaveOrganization, toggleDropdown)}
                />
              )}
            </div>
            {currentOrganization?.name && hasAccess(['owner', 'projects', 'teams', 'connectors', 'datasources']) && (
              <div
                className={`${HEADER_CLASS}__user__profile__group`}
                role="navigation"
                aria-label="Organization grouping"
              >
                <h5>{t('general.menu.sections.organization', 'Organization')}</h5>
                {hasAccess(['owner', 'projects']) && (
                  <Link
                    to={`/organization/${currentOrganization?.id}`}
                    className={`${HEADER_CLASS}__user__profile__links`}
                    onClick={toggleDropdown}
                  >
                    {t('general.menu.labels.projects', 'Projects')}
                  </Link>
                )}
                {hasAccess(['owner', 'teams']) && (
                  <Link
                    to={`/organization/${currentOrganization?.id}/directory`}
                    className={`${HEADER_CLASS}__user__profile__links`}
                    onClick={toggleDropdown}
                  >
                    {t('general.menu.labels.directory', 'Directory')}
                  </Link>
                )}
                {hasAccess(['owner', 'connectors', 'datasources']) && (
                  <Link
                    to={`/organization/${currentOrganization?.id}/integrations/v1/`}
                    className={`${HEADER_CLASS}__user__profile__links`}
                    onClick={toggleDropdown}
                  >
                    {t('general.menu.labels.integrations', 'Integrations')} (v2)
                  </Link>
                )}
                {hasAccess(['owner']) && (
                  <Link
                    to={`/organization/${currentOrganization?.id}/settings`}
                    className={`${HEADER_CLASS}__user__profile__links`}
                    onClick={toggleDropdown}
                  >
                    {t('general.menu.labels.about-org', 'About this organization')}
                  </Link>
                )}
                {hasAccess(['projects', 'teams', 'connectors', 'datasources']) && (
                  <FlightButton
                    className={`${HEADER_CLASS}__user__profile__links`}
                    label={t('general.menu.labels.leave-org', 'Leave this organization')}
                    theme="link"
                    onClick={clickLeaveOrganization}
                    onKeyDown={handleKeyDownHelper(clickLeaveOrganization, toggleDropdown)}
                  />
                )}
              </div>
            )}
            <FlightButton
              className={`${HEADER_CLASS}__user__profile__signout`}
              label={t('general.menu.labels.signout', 'Sign out')}
              theme="link"
              onClick={handleInitSignOut}
              onKeyDown={handleKeyDownHelper(handleInitSignOut, toggleDropdown)}
            />
          </div>
        </FlightDropdown>
        <FlightModal
          isVisible={isLeavingOrganization}
          toggleModalShown={() => {
            setLeavingOrganization(false);
            resetErrors();
          }}
          scrollable={false}
          size="small"
          header={
            <span>
              {t(
                'translation:pages.organizations.details.modal_leave.header',
                'Are you sure you want to leave this organization?',
              )}
            </span>
          }
          content={
            <span>
              {isLeavingLoading ? (
                <div className={LOADING_CLASS} role="alert">
                  <LoadingIcon width={80} height={80} visible={isLeavingLoading} />
                  <h3>{t('translation:pages.organizations.details.modal_leave.please_wait', 'Please wait...')}</h3>
                </div>
              ) : (
                <>
                  <p>
                    {t(
                      'translation:pages.organizations.details.leave-text',
                      'Leaving an organization will prevent you from accessing projects and other functions.',
                    )}
                  </p>
                  {leaveOrgError && (
                    <div className="form__error" role="alert">
                      {leaveOrgError}
                    </div>
                  )}
                  <br />
                  <div className="modal_buttons">
                    <FlightButton
                      type="secondary"
                      size="medium"
                      theme="secondary"
                      disabled={isLeavingLoading}
                      loading={isLeavingLoading}
                      label={t('translation:general.buttons.cancel', 'Cancel')}
                      ariaLabel={t('translation:general.buttons.cancel', 'Cancel')}
                      onClick={() => {
                        setLeavingOrganization(false);
                        resetErrors();
                      }}
                    />
                    <FlightButton
                      type="primary"
                      size="medium"
                      label={t('translation:pages.organizations.details.btn-leave', 'Leave Organization')}
                      ariaLabel={t('translation:pages.organizations.details.btn-leave', 'Leave Organization')}
                      className={`${CONTENT_CLASS}__button_delete`} // Apply style here
                      disabled={isLeavingLoading}
                      loading={isLeavingLoading}
                      onClick={leaveOrganization}
                    />
                  </div>
                </>
              )}
            </span>
          }
        />
      </div>
    </div>
  );
};
export default NavBarMenu;
